import { useState, useEffect } from 'react'
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useParams } from 'react-router-dom'
import Header from './components/Header'
import Landing from './pages/Landing'
import POSPage from './components/POS/POSPage'
import Admin from './pages/Admin'
import { getProfiles } from './services/api'

function Login({ onLogin }) {
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const navigate = useNavigate()
  
  const handleSubmit = async (e) => {
    e.preventDefault()
    try {
      const response = await fetch('/api/admin/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password })
      })
      if (response.ok) {
        onLogin()
        navigate('/admin')
      } else {
        setError('Falsches Passwort')
      }
    } catch (err) {
      setError('Fehler bei der Anmeldung')
    }
  }
  
  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
      <form onSubmit={handleSubmit} style={{ padding: '40px', background: '#fff', borderRadius: '10px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
        <h2 style={{ marginBottom: '20px', textAlign: 'center' }}>🔐 Admin Login</h2>
        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Passwort"
          style={{ padding: '12px', width: '200px', fontSize: '16px', border: '1px solid #ddd', borderRadius: '8px' }}
        />
        <button type="submit" style={{ marginLeft: '10px', padding: '12px 20px', background: '#4ecdc4', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer' }}>
          Login
        </button>
        {error && <p style={{ color: 'red', marginTop: '10px' }}>{error}</p>}
      </form>
    </div>
  )
}

function KasseWrapper({ showEdit, setShowEdit, toggleEdit, showOverview, setShowOverview }) {
  const { profileId } = useParams()
  const [profiles, setProfiles] = useState([])
  const [selectedProfile, setSelectedProfile] = useState(null)
  const [isMobile, setIsMobile] = useState(false)
  const navigate = useNavigate()
  
  const handleToggleEdit = toggleEdit || (() => setShowEdit && setShowEdit(prev => !prev))
  
  useEffect(() => {
    // Check mobile
    const checkMobile = () => setIsMobile(window.innerWidth <= 768)
    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])
  
  useEffect(() => {
    // Load profiles
    getProfiles().then(data => {
      setProfiles(data)
      
      // Find selected profile
      let profile = null
      if (profileId) {
        profile = data.find(p => p.id === profileId)
      } else {
        // Use default profile
        profile = data.find(p => p.isDefault)
      }
      
      if (profile) {
        setSelectedProfile(profile)
      } else if (data.length > 0) {
        // No default, use first profile
        setSelectedProfile(data[0])
      }
    }).catch((err) => {
      console.error('Failed to load profiles:', err)
    })
  }, [profileId])
  
  if (!selectedProfile) {
    return <div>Laden...</div>
  }
  
  return (
    <POSPage
      profile={selectedProfile}
      isMobile={isMobile}
      showOverview={showOverview}
      setShowOverview={setShowOverview}
      showEdit={showEdit}
      setShowEdit={setShowEdit}
    />
  )
}

function KasseWrapperWithSettings() {
  const [showEdit, setShowEdit] = useState(false)
  const [showOverview, setShowOverview] = useState(false)
  const toggleEdit = () => setShowEdit(prev => !prev)
  const navigate = useNavigate()

  const settingsButton = (
    <button 
      onClick={toggleEdit}
      style={{background:'transparent',border:'none',color:'#fff',fontSize:'20px',cursor:'pointer'}}
      title="Einstellungen"
    >
      ⚙️
    </button>
  )

  // Context-sensitive back handler
  const handleBack = showOverview 
    ? () => setShowOverview(false)  // Zurück zur Artikelübersicht
    : () => navigate('/')  // Zurück zur Landingpage

  // Handle browser back button
  useEffect(() => {
    if (showOverview) {
      // URL manipulieren, um den Browser-Zurück-Button abzufangen
      window.history.pushState({ showOverview: true }, '', window.location.href)
    }
  }, [showOverview])

  useEffect(() => {
    const handlePopState = (event) => {
      if (showOverview) {
        // Wenn wir auf der Übersicht sind und der Benutzer zurück will
        setShowOverview(false)
        // Verhindern, dass der Browser tatsächlich zurück navigiert
        window.history.pushState({ showOverview: false }, '', window.location.href)
      }
    }
    
    window.addEventListener('popstate', handlePopState)
    return () => window.removeEventListener('popstate', handlePopState)
  }, [showOverview, setShowOverview])

  return (
    <>
      <Header title="Kasse" onBack={handleBack} rightContent={settingsButton} />
      <KasseWrapper 
        showEdit={showEdit} 
        setShowEdit={setShowEdit} 
        toggleEdit={toggleEdit} 
        showOverview={showOverview} 
        setShowOverview={setShowOverview} 
      />
    </>
  )
}

function AdminWrapper({ isLoggedIn }) {
  const navigate = useNavigate()
  const handleBack = () => navigate('/')
  
  return (
    <>
      <Header title="Admin" onBack={handleBack} />
      <Admin onBack={handleBack} isLoggedIn={isLoggedIn} />
    </>
  )
}

function LoginPageWithNav({ onLogin }) {
  const navigate = useNavigate()
  const handleBack = () => navigate('/')
  
  return (
    <>
      <Header title="Login" onBack={handleBack} />
      <Login onLogin={onLogin} />
    </>
  )
}

function LandingWrapper() {
  const navigate = useNavigate()
  const [isLoggedIn, setIsLoggedIn] = useState(false)
  
  useEffect(() => {
    // Check if already logged in
    const session = localStorage.getItem('admin_session')
    if (session) setIsLoggedIn(true)
  }, [])
  
  const handleAdmin = () => {
    if (isLoggedIn) {
      navigate('/admin')
    } else {
      navigate('/login')
    }
  }
  
  return (
    <Landing
      onSelectProfile={(profile) => navigate(`/kasse/${profile.id}`)}
      onAdmin={handleAdmin}
    />
  )
}

export default function App() {
  const [isLoggedIn, setIsLoggedIn] = useState(() => {
    return localStorage.getItem('admin_session') === 'true'
  })
  const [sessionVerified, setSessionVerified] = useState(false)
  
  // Verify server session on mount
  useEffect(() => {
    fetch('/api/admin/session')
      .then(res => res.json())
      .then(data => {
        if (data.authenticated) {
          // Server has valid session, ensure localStorage matches
          localStorage.setItem('admin_session', 'true')
          setIsLoggedIn(true)
        } else {
          // Server session invalid, clear localStorage
          localStorage.removeItem('admin_session')
          setIsLoggedIn(false)
        }
      })
      .catch(() => {
        // Network error, keep localStorage state
      })
      .finally(() => {
        setSessionVerified(true)
      })
  }, [])
  
  const handleLogin = () => {
    setIsLoggedIn(true)
    localStorage.setItem('admin_session', 'true')
  }
  
  const handleLogout = async () => {
    setIsLoggedIn(false)
    localStorage.removeItem('admin_session')
    // Also clear server session
    await fetch('/api/admin/logout', { method: 'POST' })
  }
  
  // Don't render routes until session is verified
  if (!sessionVerified) {
    return <div>Laden...</div>
  }
  
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={
          <>
            <Header title="MV-Kasse" noBack={true} onAdminClick={handleLogout} />
            <LandingWrapper />
          </>
        } />
        <Route path="/login" element={
          <LoginPageWithNav onLogin={handleLogin} />
        } />
        <Route path="/admin" element={
          isLoggedIn ? (
            <AdminWrapper isLoggedIn={isLoggedIn} />
          ) : (
            <Navigate to="/login" />
          )
        } />
        <Route path="/kasse" element={
          <>
            <KasseWrapperWithSettings />
          </>
        } />
        <Route path="/kasse/:profileId" element={
          <>
            <KasseWrapperWithSettings />
          </>
        } />
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
    </BrowserRouter>
  )
}